import { User, Ticket, Message, Category } from '../types';

// Removed unused ApiResponse interface

class ApiService {
  private baseURL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001') + '/api';
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      this.setToken(savedToken);
    }
    console.log('🌐 ApiService initialized with backend URL:', this.baseURL);
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}, skipLogoutOn401: boolean = false): Promise<Response> {
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');
    
    console.log('API: Making request to:', `${this.baseURL}${url}`);
    console.log('API: Request method:', options.method || 'GET');
    console.log('API: Has token:', !!this.token);
    
    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
      console.log('API: Authorization header set with token');
    } else {
      console.warn('API: No token available for authenticated request');
    }

    const response = await fetch(`${this.baseURL}${url}`, {
      ...options,
      headers,
    });

    console.log('API: Response received, status:', response.status);

    // Handle 401 errors by logging out (but not during login requests or if explicitly skipped)
    if (response.status === 401 && !url.includes('/auth/login') && !skipLogoutOn401) {
      console.error('API: Authentication failed, logging out');
      this.logout();
      throw new Error('Authentication failed');
    }

    return response;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    console.log('API Response status:', response.status);
    console.log('API Response headers:', response.headers);
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      console.error('API Error - Status:', response.status, 'Response:', errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        console.error('Parsed error data:', errorData);
        errorMessage = errorData.error?.message || errorData.message || errorMessage;
      } catch {
        // If not JSON, use the text as error message
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const text = await response.text();
    console.log('API Response text:', text);
    
    if (!text) return {} as T;
    
    try {
      const parsed = JSON.parse(text);
      console.log('API Parsed response:', parsed);
      return parsed;
    } catch (parseError) {
      console.error('Failed to parse JSON response:', text);
      console.error('Parse error:', parseError);
      throw new Error('Invalid JSON response');
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  logout() {
    this.token = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    // Don't redirect here - let the app handle routing
  }

  // Token validation
  async validateToken(): Promise<{ user: User; tokenValid: boolean }> {
    console.log('API: Validating current token...');
    
    const response = await this.fetchWithAuth('/auth/validate', {
      method: 'GET',
    });
    
    const apiResponse = await this.handleResponse<{
      success: boolean;
      data: {
        user: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          userType: 'customer' | 'agent';
          roleId?: string;
          roleName?: string;
          permissions?: string[];
          avatarUrl?: string;
        };
        tokenValid: boolean;
      };
    }>(response);
    
    const user: User = {
      id: apiResponse.data.user.id,
      email: apiResponse.data.user.email,
      name: `${apiResponse.data.user.firstName} ${apiResponse.data.user.lastName}`,
      firstName: apiResponse.data.user.firstName,
      lastName: apiResponse.data.user.lastName,
      role: apiResponse.data.user.userType,
      userType: apiResponse.data.user.userType,
      roleId: apiResponse.data.user.roleId,
      roleName: apiResponse.data.user.roleName,
      permissions: apiResponse.data.user.permissions || [],
      avatarUrl: apiResponse.data.user.avatarUrl || undefined,
    };
    
    return { user, tokenValid: apiResponse.data.tokenValid };
  }

  // Simplified login for debugging
  async loginSimple(email: string, password: string): Promise<{ user: User; token: string }> {
    console.log('API: Using simplified login for', email);
    
    const response = await this.fetchWithAuth('/auth/login-simple', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    const apiResponse = await this.handleResponse<{
      success: boolean;
      data: {
        user: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          userType: 'customer' | 'agent';
          roleId?: string;
          roleName?: string;
          permissions?: string[];
        };
        tokens: {
          accessToken: string;
          expiresIn: number;
        };
      };
    }>(response);
    
    const user: User = {
      id: apiResponse.data.user.id,
      email: apiResponse.data.user.email,
      name: `${apiResponse.data.user.firstName} ${apiResponse.data.user.lastName}`,
      firstName: apiResponse.data.user.firstName,
      lastName: apiResponse.data.user.lastName,
      role: apiResponse.data.user.userType,
      userType: apiResponse.data.user.userType,
      roleId: apiResponse.data.user.roleId,
      roleName: apiResponse.data.user.roleName,
      permissions: apiResponse.data.user.permissions || [],
    };
    
    const token = apiResponse.data.tokens.accessToken;
    
    this.setToken(token);
    localStorage.setItem('user_data', JSON.stringify(user));
    
    return { user, token };
  }

  // Auth APIs
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    console.log('API: Sending login request to', `${this.baseURL}/auth/login`);
    
    const response = await this.fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    console.log('API: Login response status:', response.status);
    
    const apiResponse = await this.handleResponse<{
      success: boolean;
      data: {
        user: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          userType: 'customer' | 'agent';
          agentStatus?: string;
          roleId?: string;
          roleName?: string;
          permissions?: string[];
          avatarUrl?: string;
        };
        tokens: {
          accessToken: string;
          expiresIn: number;
        };
      };
    }>(response);
    
    console.log('API: Login successful, received data:', apiResponse);
    
    // Transform the response to match frontend expectations
    const user: User = {
      id: apiResponse.data.user.id,
      email: apiResponse.data.user.email,
      name: `${apiResponse.data.user.firstName} ${apiResponse.data.user.lastName}`,
      firstName: apiResponse.data.user.firstName,
      lastName: apiResponse.data.user.lastName,
      role: apiResponse.data.user.userType,
      userType: apiResponse.data.user.userType,
      agentStatus: apiResponse.data.user.agentStatus as 'online' | 'busy' | 'offline' | undefined,
      roleId: apiResponse.data.user.roleId,
      roleName: apiResponse.data.user.roleName,
      permissions: apiResponse.data.user.permissions || [],
      avatarUrl: apiResponse.data.user.avatarUrl || undefined,
    };
    
    const token = apiResponse.data.tokens.accessToken;
    
    this.setToken(token);
    localStorage.setItem('user_data', JSON.stringify(user));
    
    return { user, token };
  }

  async register(name: string, email: string, password: string): Promise<{ user: User; token: string }> {
    const response = await this.fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
    
    const data = await this.handleResponse<{ user: User; token: string }>(response);
    this.setToken(data.token);
    localStorage.setItem('user_data', JSON.stringify(data.user));
    return data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.fetchWithAuth('/auth/me');
    const data = await this.handleResponse<{ user: User }>(response);
    return data.user;
  }

  // Ticket APIs
  async getTickets(filters?: { 
    status?: string; 
    priority?: string; 
    category?: string; 
    search?: string; 
    page?: number; 
    limit?: number; 
  }): Promise<{ tickets: Ticket[]; pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number; } }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    const queryString = params.toString();
    const url = `/tickets${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { tickets: any[]; pagination: any } }>(response);
    
    // Transform tickets to match frontend interface
    const tickets: Ticket[] = apiResponse.data.tickets.map((ticket: any) => {
      console.log(`🎫 Processing ticket ${ticket.id}:`, {
        hasMessages: !!ticket.messages,
        messageCount: ticket.messages?.length || 0,
        rawMessages: ticket.messages
      });
      
      return {
        id: ticket.id,
        ticketNumber: ticket.ticketNumber || ticket.id,
        title: ticket.title,
        description: ticket.description,
        status: ticket.status,
        priority: ticket.priority,
        category: ticket.category?.name || ticket.categoryId,
        customerId: ticket.customerId,
        customerName: ticket.customerName,
        customerEmail: ticket.customerEmail,
        customerPhone: ticket.customerPhone,
        customerCompany: ticket.customerCompany,
        customerAddress: ticket.customerAddress,
        customerStreetAddress: ticket.customerStreetAddress,
        customerCity: ticket.customerCity,
        customerState: ticket.customerState,
        customerZipCode: ticket.customerZipCode,
        customerCountry: ticket.customerCountry,
        customerType: ticket.customerType || 'Standard',
        deviceModel: ticket.deviceModel,
        deviceSerialNumber: ticket.deviceSerialNumber,
        categoryId: ticket.categoryId,
        agentId: ticket.agentId,
        companyId: ticket.companyId,
        aiEnabled: ticket.aiEnabled,
        createdAt: ticket.createdAt,
        updatedAt: ticket.updatedAt,
        // Add AI summary fields with proper snake_case to camelCase mapping
        resolutionSummary: ticket.resolution_summary || ticket.resolutionSummary,
        resolutionSummaryGeneratedAt: ticket.resolution_summary_generated_at || ticket.resolutionSummaryGeneratedAt,
        resolutionSummaryModelVersion: ticket.resolution_summary_model_version || ticket.resolutionSummaryModelVersion,
        resolutionSummaryGeneratedBy: ticket.resolution_summary_generated_by || ticket.resolutionSummaryGeneratedBy,
        // Add AI quality evaluation fields
        aiQualityEvaluation: ticket.ai_quality_evaluation ? (typeof ticket.ai_quality_evaluation === 'string' ? JSON.parse(ticket.ai_quality_evaluation) : ticket.ai_quality_evaluation) : ticket.aiQualityEvaluation,
        aiQualityResolutionScore: ticket.ai_quality_resolution_score || ticket.aiQualityResolutionScore,
        aiQualityCourtesyScore: ticket.ai_quality_courtesy_score || ticket.aiQualityCourtesyScore,
        aiQualityTimelinessScore: ticket.ai_quality_timeliness_score || ticket.aiQualityTimelinessScore,
        aiQualityOverallRating: ticket.ai_quality_overall_rating || ticket.aiQualityOverallRating,
        customer: ticket.customer || null,
        agent: ticket.agent || null,
        messages: ticket.messages || [],
        lastMessageAt: ticket.lastMessageAt || ticket.createdAt,
        messageCount: ticket.messageCount || 0,
        hasServiceWorkflow: ticket.hasServiceWorkflow || false
      };
    });
    
    return {
      tickets,
      pagination: apiResponse.data.pagination
    };
  }

  // Get tickets with service workflows
  async getTicketsWithServices(filters?: { 
    status?: string; 
    priority?: string; 
    category?: string; 
    search?: string; 
    page?: number; 
    limit?: number; 
  }): Promise<{ tickets: any[]; pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number; } }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    // Add filter to only get tickets with service workflows
    params.append('hasServiceWorkflow', 'true');
    
    const queryString = params.toString();
    const url = `/tickets${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { tickets: any[]; pagination: any } }>(response);
    
    // Transform tickets to match frontend interface and include service workflow data
    const ticketsWithServices = await Promise.all(apiResponse.data.tickets.map(async (ticket: any) => {
      // Get service workflows for this ticket
      let serviceWorkflows = [];
      try {
        const workflowResponse = await this.get(`/tickets/${ticket.id}/service-workflows`);
        serviceWorkflows = Array.isArray(workflowResponse) ? workflowResponse : [];
      } catch (error) {
        console.error(`Error fetching service workflows for ticket ${ticket.id}:`, error);
      }
      
      return {
        id: ticket.id,
        ticketNumber: ticket.ticketNumber || ticket.id,
        title: ticket.title,
        description: ticket.description,
        status: ticket.status,
        priority: ticket.priority,
        category: ticket.category?.name || ticket.categoryId,
        customerId: ticket.customerId,
        customerName: ticket.customerName,
        customerEmail: ticket.customerEmail,
        customerPhone: ticket.customerPhone,
        customerCompany: ticket.customerCompany,
        customerAddress: ticket.customerAddress,
        customerStreetAddress: ticket.customerStreetAddress,
        customerCity: ticket.customerCity,
        customerState: ticket.customerState,
        customerZipCode: ticket.customerZipCode,
        customerCountry: ticket.customerCountry,
        customerType: ticket.customerType || 'Standard',
        deviceModel: ticket.deviceModel,
        deviceSerialNumber: ticket.deviceSerialNumber,
        categoryId: ticket.categoryId,
        agentId: ticket.agentId,
        companyId: ticket.companyId,
        aiEnabled: ticket.aiEnabled,
        createdAt: ticket.createdAt,
        updatedAt: ticket.updatedAt,
        // Add service workflow data
        serviceWorkflows: serviceWorkflows,
        hasServiceWorkflow: serviceWorkflows.length > 0,
        // Map messages with proper structure
        messages: (ticket.messages || []).map((msg: any) => ({
          id: msg.id,
          content: msg.content,
          createdAt: msg.createdAt,
          userId: msg.userId,
          senderId: msg.senderId,
          sender: msg.sender
        }))
      };
    }));

    return {
      tickets: ticketsWithServices,
      pagination: apiResponse.data.pagination
    };
  }

  async getTicketById(id: string): Promise<Ticket> {
    const response = await this.fetchWithAuth(`/tickets/${id}`);
    const data = await this.handleResponse<{ ticket: any }>(response);
    
    // Transform snake_case fields to camelCase for consistency
    const ticket = data.ticket;
    console.log('🔧 API: getTicketById raw ticket data:', ticket);
    console.log('🔧 API: getTicketById deviceModel:', ticket.deviceModel);
    console.log('🔧 API: getTicketById deviceSerialNumber:', ticket.deviceSerialNumber);
    console.log('🔧 API: getTicketById device_model:', ticket.device_model);
    console.log('🔧 API: getTicketById device_serial_number:', ticket.device_serial_number);
    
    const transformedTicket: Ticket = {
      ...ticket,
      // Add device fields with proper snake_case to camelCase mapping
      deviceModel: ticket.deviceModel || ticket.device_model,
      deviceSerialNumber: ticket.deviceSerialNumber || ticket.device_serial_number,
      linkedDeviceId: ticket.linkedDeviceId || ticket.linked_device_id,
      // Add AI summary fields with proper snake_case to camelCase mapping
      resolutionSummary: ticket.resolution_summary || ticket.resolutionSummary,
      resolutionSummaryGeneratedAt: ticket.resolution_summary_generated_at || ticket.resolutionSummaryGeneratedAt,
      resolutionSummaryModelVersion: ticket.resolution_summary_model_version || ticket.resolutionSummaryModelVersion,
      resolutionSummaryGeneratedBy: ticket.resolution_summary_generated_by || ticket.resolutionSummaryGeneratedBy,
      // Add AI quality evaluation fields
      aiQualityEvaluation: ticket.ai_quality_evaluation ? (typeof ticket.ai_quality_evaluation === 'string' ? JSON.parse(ticket.ai_quality_evaluation) : ticket.ai_quality_evaluation) : ticket.aiQualityEvaluation,
      aiQualityResolutionScore: ticket.ai_quality_resolution_score || ticket.aiQualityResolutionScore,
      aiQualityCourtesyScore: ticket.ai_quality_courtesy_score || ticket.aiQualityCourtesyScore,
      aiQualityTimelinessScore: ticket.ai_quality_timeliness_score || ticket.aiQualityTimelinessScore,
      aiQualityOverallRating: ticket.ai_quality_overall_rating || ticket.aiQualityOverallRating,
    };
    
    return transformedTicket;
  }

  async createTicket(ticketData: any, customerInfo?: { name: string; email: string; company?: string; phone?: string; country?: string }): Promise<Ticket> {
    console.log('API: Creating ticket with data:', ticketData);
    console.log('API: Customer info:', customerInfo);
    
    const payload = {
      ...ticketData,
      ...(customerInfo && { customerInfo })
    };
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseURL}/tickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    
    console.log('API: Create ticket response status:', response.status);
    
    const apiResponse = await this.handleResponse<{
      success: boolean;
      data: {
        ticket: {
          id: string;
          ticketNumber: string;
          title: string;
          description: string;
          status: string;
          priority: string;
          categoryId: string;
          customerId: string | null;
          customerName: string;
          customerEmail: string;
          customerPhone: string | null;
          customerCompany: string | null;
          customerAddress: string | null;
          agentId: string | null;
          createdAt: string;
          updatedAt: string;
          isAnonymous: boolean;
          customer: {
            id?: string;
            firstName?: string;
            lastName?: string;
            name?: string;
            email?: string;
            company?: string;
          };
          category: {
            id: string;
            name: string;
            description: string;
          };
        };
      };
    }>(response);
    
    console.log('API: Create ticket successful, received data:', apiResponse);
    
    // Transform the response to match frontend expectations
    // Backend returns snake_case field names, so we need to map them correctly
    const ticket = apiResponse.data.ticket as any;
    const createdTicket: Ticket = {
      id: ticket.ticket_id || ticket.id,
      ticketNumber: ticket.ticket_number || ticket.ticketNumber || ticket.ticket_id,
      title: ticket.title,
      description: ticket.description,
      status: ticket.status as 'new' | 'in_progress' | 'closed-resolved' | 'closed-not-resolved' | 'closed-partially-resolved' | 'closed-no-feedback' | 'closed-by-agent-resolved',
      priority: ticket.priority as 'low' | 'medium' | 'high',
      category: ticket.category?.name || ticket.category_name,
      customerId: ticket.customer_id || ticket.customerId || '',
      customerName: ticket.customer_name || ticket.customerName,
      customerEmail: ticket.customer_email || ticket.customerEmail,
      customerPhone: ticket.customer_phone || ticket.customerPhone || undefined,
      customerCompany: ticket.customer_company || ticket.customerCompany || undefined,
      customerAddress: ticket.customer_address || ticket.customerAddress || undefined,
      customerStreetAddress: ticket.customer_street_address || ticket.customerStreetAddress || undefined,
      customerCity: ticket.customer_city || ticket.customerCity || undefined,
      customerState: ticket.customer_state || ticket.customerState || undefined,
      customerZipCode: ticket.customer_zip_code || ticket.customerZipCode || undefined,
      customerCountry: ticket.customer_country || ticket.customerCountry || undefined,
      customerType: ticket.customer_type || ticket.customerType || 'Standard',
      deviceModel: ticket.device_model || ticket.deviceModel || undefined,
      deviceSerialNumber: ticket.device_serial_number || ticket.deviceSerialNumber || undefined,
      agentId: ticket.agent_id || ticket.agentId || undefined,
      createdAt: ticket.created_at || ticket.createdAt,
      updatedAt: ticket.updated_at || ticket.updatedAt,
      customer: ticket.customer ? {
        id: ticket.customer.id || '',
        email: ticket.customer_email || ticket.customerEmail,
        name: ticket.customer.name || ticket.customer_name || ticket.customerName,
        role: 'customer' as const
      } : {
        id: '',
        email: ticket.customer_email || ticket.customerEmail,
        name: ticket.customer_name || ticket.customerName,
        role: 'customer' as const
      }
    };
    
    return createdTicket;
  }

  async updateTicket(id: string, updates: Partial<Ticket>): Promise<Ticket> {
    console.log('🔧 API: updateTicket called');
    console.log('  - ticketId:', id);
    console.log('  - updates:', updates);
    console.log('  - updates.customerAddress:', updates.customerAddress);
    console.log('  - updates.customerCity:', updates.customerCity);
    console.log('  - customerAddress included:', 'customerAddress' in updates);
    console.log('  - customerCity included:', 'customerCity' in updates);
    
    // Convert camelCase field names to snake_case for backend compatibility
    const backendUpdates: any = { ...updates };
    
    // Field name conversions
    if ('deviceModel' in updates) {
      backendUpdates.device_model = updates.deviceModel;
      delete backendUpdates.deviceModel;
    }
    if ('deviceSerialNumber' in updates) {
      backendUpdates.device_serial_number = updates.deviceSerialNumber;
      delete backendUpdates.deviceSerialNumber;
      console.log('🔧 API: Converted deviceSerialNumber to device_serial_number:', updates.deviceSerialNumber);
    }
    if ('selectedDeviceId' in updates) {
      backendUpdates.selected_device_id = updates.selectedDeviceId;
      delete backendUpdates.selectedDeviceId;
    }
    
    console.log('🔧 API: Sending backend updates:', backendUpdates);
    
    const response = await this.fetchWithAuth(`/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(backendUpdates),
    });
    
    console.log('🔧 API: updateTicket response status:', response.status);
    
    const apiResponse = await this.handleResponse<{ success: boolean; data: { ticket: any } }>(response);
    
    console.log('🔧 API: updateTicket response data:', apiResponse);
    console.log('  - response ticket.customerAddress:', apiResponse.data.ticket.customerAddress);
    console.log('  - response ticket.deviceModel:', apiResponse.data.ticket.deviceModel);
    console.log('  - response ticket.deviceSerialNumber:', apiResponse.data.ticket.deviceSerialNumber);
    
    // Transform the ticket data to match frontend interface
    const ticket: Ticket = {
      id: apiResponse.data.ticket.id,
      ticketNumber: apiResponse.data.ticket.ticketNumber || apiResponse.data.ticket.id,
      title: apiResponse.data.ticket.title,
      description: apiResponse.data.ticket.description,
      status: apiResponse.data.ticket.status,
      priority: apiResponse.data.ticket.priority,
      category: apiResponse.data.ticket.category?.name || apiResponse.data.ticket.categoryId,
      customerId: apiResponse.data.ticket.customerId,
      customerName: apiResponse.data.ticket.customerName,
      customerEmail: apiResponse.data.ticket.customerEmail,
      customerPhone: apiResponse.data.ticket.customerPhone,
      customerCompany: apiResponse.data.ticket.customerCompany,
      customerAddress: apiResponse.data.ticket.customerAddress,
      customerStreetAddress: (apiResponse.data.ticket as any).customerStreetAddress,
      customerCity: (apiResponse.data.ticket as any).customerCity,
      customerState: (apiResponse.data.ticket as any).customerState,
      customerZipCode: (apiResponse.data.ticket as any).customerZipCode,
      customerCountry: (apiResponse.data.ticket as any).customerCountry,
      customerType: (apiResponse.data.ticket as any).customerType || 'Standard', // Add customerType with default
      deviceModel: (apiResponse.data.ticket as any).deviceModel,
      deviceSerialNumber: (apiResponse.data.ticket as any).deviceSerialNumber,
      agentId: apiResponse.data.ticket.agentId,
      createdAt: apiResponse.data.ticket.createdAt,
      updatedAt: apiResponse.data.ticket.updatedAt,
      customer: apiResponse.data.ticket.customer ? {
        id: apiResponse.data.ticket.customer.id,
        email: apiResponse.data.ticket.customer.email,
        name: `${apiResponse.data.ticket.customer.firstName} ${apiResponse.data.ticket.customer.lastName}`,
        role: 'customer' as const
      } : undefined,
      agent: apiResponse.data.ticket.agent ? {
        id: apiResponse.data.ticket.agent.id,
        email: apiResponse.data.ticket.agent.email,
        name: `${apiResponse.data.ticket.agent.firstName} ${apiResponse.data.ticket.agent.lastName}`,
        role: 'agent' as const
      } : undefined,
    };
    
    console.log('🔧 API: transformed ticket:', ticket);
    console.log('  - transformed ticket.customerAddress:', ticket.customerAddress);
    
    return ticket;
  }

  async claimTicket(id: string): Promise<Ticket> {
    const response = await this.fetchWithAuth(`/tickets/${id}/claim`, {
      method: 'POST',
    });
    
    const data = await this.handleResponse<{ success: boolean; data: { ticket: Ticket } }>(response);
    return data.data.ticket;
  }

  async rateTicket(id: string, rating: number, feedback?: string): Promise<void> {
    await this.fetchWithAuth(`/tickets/${id}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating, feedback }),
    });
  }

  async deleteTicket(id: string): Promise<void> {
    const response = await this.fetchWithAuth(`/tickets/${id}`, {
      method: 'DELETE',
    });
    await this.handleResponse<void>(response);
  }

  // Internal Comments APIs
  async getInternalComments(ticketId: string): Promise<any[]> {
    console.log('API: Getting internal comments for ticket', ticketId);
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/internal-comments`);
    console.log('API: Get internal comments response status:', response.status);
    const data = await this.handleResponse<{ success: boolean; data: { comments: any[] } }>(response);
    console.log('API: Get internal comments successful, received data:', data);
    return data.data.comments;
  }

  async addInternalComment(ticketId: string, content: string): Promise<any> {
    console.log('API: Adding internal comment to ticket', ticketId, 'content:', content);
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/internal-comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
    console.log('API: Add internal comment response status:', response.status);
    const data = await this.handleResponse<{ success: boolean; data: { comment: any } }>(response);
    console.log('API: Add internal comment successful, received data:', data);
    return data.data.comment;
  }

  // Message APIs
  async getMessages(ticketId: string): Promise<Message[]> {
    console.log('API: Getting messages for ticket', ticketId);
    
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/messages`);
    
    console.log('API: Get messages response status:', response.status);
    
    const apiResponse = await this.handleResponse<{
      success: boolean;
      data: {
        messages: any[];
      };
    }>(response);
    
    console.log('API: Get messages successful, received data:', apiResponse);
    
    // Transform messages to match frontend interface
    // Handle both snake_case and camelCase field names from backend
    const messages: Message[] = (apiResponse.data.messages || []).map((msg: any) => ({
      id: msg.id,
      content: msg.content,
      messageType: msg.messageType || msg.message_type || 'text',
      userId: msg.senderId || msg.sender_id,
      senderId: msg.senderId || msg.sender_id,
      ticketId: msg.ticketId || msg.ticket_id,
      createdAt: msg.createdAt || msg.created_at,
      // Preserve original sender object
      sender: msg.sender ? {
        id: msg.sender.id,
        firstName: msg.sender.firstName,
        lastName: msg.sender.lastName,
        userType: msg.sender.userType || msg.sender.user_type,
        email: msg.sender.email
      } : undefined,
      // Also keep user for backward compatibility
      user: msg.sender ? {
        id: msg.sender.id,
        email: msg.sender.email || '',
        name: msg.sender.firstName && msg.sender.lastName 
          ? `${msg.sender.firstName} ${msg.sender.lastName}`
          : msg.sender.name || 'Unknown User',
        role: (msg.sender.userType || msg.sender.user_type) as 'customer' | 'agent'
      } : undefined
    }));
    
    return messages;
  }

  async sendMessage(ticketId: string, content: string): Promise<Message> {
    console.log('API: Sending message to ticket', ticketId, 'content:', content);
    
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
    
    console.log('API: Send message response status:', response.status);
    
    const apiResponse = await this.handleResponse<{
      success: boolean;
      data: {
        message: any;
      };
    }>(response);
    
    console.log('API: Send message successful, received data:', apiResponse);
    
    // Transform message to match frontend interface
    const message: Message = {
      id: apiResponse.data.message.id,
      content: apiResponse.data.message.content,
      messageType: apiResponse.data.message.messageType || 'text',
      userId: apiResponse.data.message.senderId,
      ticketId: apiResponse.data.message.ticketId,
      createdAt: apiResponse.data.message.createdAt,
      user: apiResponse.data.message.sender ? {
        id: apiResponse.data.message.sender.id,
        email: apiResponse.data.message.sender.email || '',
        name: `${apiResponse.data.message.sender.firstName} ${apiResponse.data.message.sender.lastName}`,
        role: apiResponse.data.message.sender.userType as 'customer' | 'agent'
      } : undefined
    };
    
    return message;
  }

  async sendEmail(ticketId: string, content: string): Promise<{ message: Message; emailSent: boolean }> {
    console.log('API: Sending email for ticket', ticketId, 'content:', content);
    
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/send-email`, {
      method: 'POST',
      body: JSON.stringify({ message: content }),
    });
    
    console.log('API: Send email response status:', response.status);
    
    const apiResponse = await this.handleResponse<{
      success: boolean;
      data: {
        message: any;
        emailSent: boolean;
        emailId: string;
        logId: string;
      };
    }>(response);
    
    console.log('API: Send email successful, received data:', apiResponse);
    
    // Transform message to match frontend interface
    const message: Message = {
      id: apiResponse.data.message.id,
      content: apiResponse.data.message.content,
      messageType: apiResponse.data.message.messageType || 'text',
      userId: apiResponse.data.message.senderId,
      ticketId: apiResponse.data.message.ticketId,
      createdAt: apiResponse.data.message.createdAt,
      user: apiResponse.data.message.sender ? {
        id: apiResponse.data.message.sender.id,
        email: apiResponse.data.message.sender.email || '',
        name: `${apiResponse.data.message.sender.firstName} ${apiResponse.data.message.sender.lastName}`,
        role: apiResponse.data.message.sender.userType as 'customer' | 'agent'
      } : undefined
    };
    
    return {
      message,
      emailSent: apiResponse.data.emailSent
    };
  }

  async uploadFile(ticketId: string, file: File): Promise<{ message: Message; file: any }> {
    console.log('API: Uploading file to ticket', ticketId, 'file:', file.name);
    console.log('API: File size:', file.size, 'bytes');
    console.log('API: File type:', file.type);
    console.log('API: Has token:', !!this.token);
    
    const formData = new FormData();
    formData.append('file', file);

    // Use fetch directly for file upload (no JSON Content-Type header)
    const response = await fetch(`${this.baseURL}/tickets/${ticketId}/upload`, {
      method: 'POST',
      headers: this.token ? { 'Authorization': `Bearer ${this.token}` } : {},
      body: formData,
    });

    console.log('API: Upload file response status:', response.status);
    console.log('API: Upload file response headers:', response.headers);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('API: Upload file error response text:', errorText);
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      try {
        const errorData = JSON.parse(errorText);
        console.log('API: Upload file parsed error data:', errorData);
        errorMessage = errorData.error?.message || errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      console.log('API: Upload file final error message:', errorMessage);
      throw new Error(errorMessage);
    }

    const apiResponse = await response.json();
    console.log('API: Upload file successful, received data:', apiResponse);
    
    // Transform message to match frontend interface
    const message: Message = {
      id: apiResponse.data.message.id,
      content: apiResponse.data.message.content,
      messageType: apiResponse.data.message.messageType,
      userId: apiResponse.data.message.senderId,
      ticketId: apiResponse.data.message.ticketId,
      createdAt: apiResponse.data.message.createdAt,
      user: apiResponse.data.message.sender ? {
        id: apiResponse.data.message.sender.id,
        email: apiResponse.data.message.sender.email || '',
        name: `${apiResponse.data.message.sender.firstName} ${apiResponse.data.message.sender.lastName}`,
        role: apiResponse.data.message.sender.userType as 'customer' | 'agent'
      } : undefined,
      fileName: apiResponse.data.message.fileName,
      filePath: apiResponse.data.message.filePath,
      fileSize: apiResponse.data.message.fileSize,
      fileUrl: apiResponse.data.message.fileUrl
    };
    
    return {
      message,
      file: apiResponse.data.file
    };
  }

  async getTicketFiles(ticketId: string): Promise<any[]> {
    console.log('📁 API: Getting files for ticket:', ticketId);
    console.log('📁 API: Current auth token:', localStorage.getItem('auth_token') ? 'Present' : 'Missing');
    console.log('📁 API: Full URL:', `${this.baseURL}/tickets/${ticketId}/files`);
    console.log('📁 API: Base URL:', this.baseURL);
    console.log('📁 API: Request path:', `/tickets/${ticketId}/files`);
    
    try {
      const response = await this.fetchWithAuth(`/tickets/${ticketId}/files`);
      console.log('📁 API: Response status:', response.status);
      console.log('📁 API: Response headers:', Object.fromEntries(response.headers.entries()));
      
      const result = await this.handleResponse<{ success: boolean; data: { files: any[] } }>(response);
      
      console.log('📁 API: Retrieved files result:', result);
      console.log('📁 API: Files array:', result.data.files);
      console.log('📁 API: Files count:', result.data.files.length);
      return result.data.files;
      
    } catch (error) {
      console.error('📁 API: Error getting files:', error);
      console.error('📁 API: Error details:', {
        name: (error as Error).name,
        message: (error as Error).message,
        stack: (error as Error).stack
      });
      throw error;
    }
  }

  // Get all files across the system
  async getAllFiles(filters?: {
    search?: string;
    fileType?: string;
    ticketId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ files: any[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    const queryString = params.toString();
    const url = `/files/all${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    const result = await this.handleResponse<{ success: boolean; data: { files: any[]; pagination: any } }>(response);
    
    return result.data;
  }

  // Get file statistics
  async getFileStats(): Promise<any> {
    const response = await this.fetchWithAuth('/files/stats');
    const result = await this.handleResponse<{ success: boolean; data: any }>(response);
    return result.data;
  }

  // Delete a file
  async deleteFile(fileId: string): Promise<void> {
    const response = await this.fetchWithAuth(`/files/${fileId}`, {
      method: 'DELETE'
    });
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }

  // Email APIs
  async getTicketEmails(ticketId: string): Promise<any[]> {
    console.log('📧 API: Getting emails for ticket:', ticketId);
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/emails`);
    console.log('📧 API: Response status:', response.status);
    
    const result = await this.handleResponse<{ success: boolean; data: any[] }>(response);
    console.log('📧 API: Retrieved emails result:', result);
    return result.data;
  }

  // Email Debug APIs
  async manualFetchEmails(): Promise<any> {
    console.log('🔧 API: Manual email fetch requested');
    const response = await this.fetchWithAuth('/debug/fetch-emails', {
      method: 'POST'
    }, true); // Skip logout on 401 - this is a background service
    const result = await this.handleResponse<any>(response);
    console.log('🔧 API: Manual fetch result:', result);
    return result;
  }

  async getEmailStatus(): Promise<any> {
    console.log('📊 API: Getting email status');
    const response = await this.fetchWithAuth('/debug/email-status');
    const result = await this.handleResponse<any>(response);
    console.log('📊 API: Email status:', result);
    return result;
  }

  // Categories API
  async getCategories(): Promise<Category[]> {
    const response = await this.fetchWithAuth('/categories');
    const apiResponse = await this.handleResponse<{ success: boolean; data: { categories: any[] } }>(response);
    
    // Transform categories to match frontend interface
    const categories: Category[] = apiResponse.data.categories.map((cat: any) => ({
      id: cat.id,
      name: cat.name,
      description: cat.description
    }));
    
    return categories;
  }

  async createCategory(categoryData: { name: string; description: string; colorCode?: string }): Promise<Category> {
    const response = await this.fetchWithAuth('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { category: any } }>(response);
    
    return {
      id: apiResponse.data.category.id,
      name: apiResponse.data.category.name,
      description: apiResponse.data.category.description
    };
  }

  async updateCategory(id: string, categoryData: { name?: string; description?: string; colorCode?: string }): Promise<Category> {
    const response = await this.fetchWithAuth(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { category: any } }>(response);
    
    return {
      id: apiResponse.data.category.id,
      name: apiResponse.data.category.name,
      description: apiResponse.data.category.description
    };
  }

  async deleteCategory(id: string): Promise<void> {
    const response = await this.fetchWithAuth(`/categories/${id}`, {
      method: 'DELETE'
    });
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }

  // System Settings API (Admin only)
  async getSystemSettings(): Promise<any> {
    const response = await this.fetchWithAuth('/system-settings');
    const apiResponse = await this.handleResponse<{ success: boolean; data: { settings: any } }>(response);
    return apiResponse.data.settings;
  }

  async updateSystemSettings(settings: any): Promise<any> {
    const response = await this.fetchWithAuth('/system-settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { settings: any } }>(response);
    return apiResponse.data.settings;
  }

  // Get public system settings for components
  async getPublicSystemSettings(): Promise<any> {
    const response = await this.fetchWithAuth('/system-settings/public');
    const apiResponse = await this.handleResponse<{ success: boolean; data: { settings: any } }>(response);
    return apiResponse.data.settings;
  }

  // Daily Digest API methods (Admin only)
  async getDigestStatus(): Promise<any> {
    const response = await this.fetchWithAuth('/admin/digest/status');
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    return apiResponse.data;
  }

  async sendDigestToAllAgents(): Promise<any> {
    const response = await this.fetchWithAuth('/admin/digest/send-all', {
      method: 'POST'
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    return apiResponse.data;
  }

  async sendTestDigest(): Promise<any> {
    const response = await this.fetchWithAuth('/digest/test', {
      method: 'POST'
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    return apiResponse.data;
  }

  // AI Agent System API (Admin only)
  async getAiAgentConfig(): Promise<any> {
    const response = await this.fetchWithAuth('/ai-agent/config');
    const apiResponse = await this.handleResponse<{ success: boolean; data: { config: any } }>(response);
    return apiResponse.data.config;
  }

  async updateAiAgentConfig(config: any): Promise<any> {
    const response = await this.fetchWithAuth('/ai-agent/config', {
      method: 'PUT',
      body: JSON.stringify(config)
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { config: any } }>(response);
    return apiResponse.data.config;
  }

  async getAiDocuments(): Promise<any[]> {
    const response = await this.fetchWithAuth('/ai-agent/documents');
    const apiResponse = await this.handleResponse<{ success: boolean; data: { documents: any[] } }>(response);
    return apiResponse.data.documents;
  }

  async uploadAiDocument(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('document', file);
    
    const response = await fetch(`${this.baseURL}/ai-agent/documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });

    const apiResponse = await this.handleResponse<{ success: boolean; data: { document: any } }>(response);
    return apiResponse.data.document;
  }

  async uploadAiDocuments(files: FileList): Promise<any> {
    const formData = new FormData();
    
    // Append all files to the same field name 'documents'
    for (let i = 0; i < files.length; i++) {
      formData.append('documents', files[i]);
    }
    
    const response = await fetch(`${this.baseURL}/ai-agent/documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });

    const apiResponse = await this.handleResponse<{ 
      success: boolean; 
      data: { 
        uploadedDocuments: any[]; 
        failedDocuments: any[] 
      } 
    }>(response);
    return apiResponse.data;
  }

  async deleteAiDocument(documentId: string): Promise<void> {
    const response = await this.fetchWithAuth(`/ai-agent/documents/${documentId}`, {
      method: 'DELETE'
    });
    await this.handleResponse<{ success: boolean }>(response);
  }

  async getAiStats(): Promise<any> {
    const response = await this.fetchWithAuth('/ai-agent/stats');
    const apiResponse = await this.handleResponse<{ success: boolean; data: { stats: any } }>(response);
    return apiResponse.data.stats;
  }

  async toggleTicketAi(ticketId: string, enabled: boolean, reason?: string): Promise<any> {
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/toggle-ai`, {
      method: 'POST',
      body: JSON.stringify({ enabled, reason })
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    return apiResponse.data;
  }

  async generateTicketSummary(ticketId: string): Promise<any> {
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/generate-summary`, {
      method: 'POST'
    });
    const apiResponse = await this.handleResponse<{ 
      success: boolean; 
      data: { 
        summary: string;
        generatedAt: string;
        modelVersion: string;
        generatedBy: string;
      } 
    }>(response);
    return apiResponse.data;
  }

  // Dropdown Options Management APIs
  async getDropdownOptions(): Promise<{
    categories: Category[];
    deviceModels: any[];
    customerTypes: any[];
  }> {
    try {
      // Try the combined endpoint first (requires Admin role)
      const response = await this.fetchWithAuth('/dropdown-options');
      const data = await this.handleResponse<{ 
        success: boolean; 
        data: {
          categories: Category[];
          deviceModels: any[];
          customerTypes: any[];
        }
      }>(response);
      return data.data;
    } catch (error) {
      console.log('Combined dropdown-options endpoint failed, falling back to individual endpoints:', (error as Error).message);
      
      // Fallback: Get data from individual endpoints
      try {
        const [categoriesData, deviceModelsData, customerTypesData] = await Promise.all([
          this.getCategories(),
          this.getDeviceModelsDirect(),
          this.getCustomerTypesDirect()
        ]);
        
        return {
          categories: categoriesData,
          deviceModels: deviceModelsData,
          customerTypes: customerTypesData
        };
      } catch (fallbackError) {
        console.error('All dropdown options endpoints failed:', fallbackError);
        throw new Error(`Failed to load dropdown options: ${(fallbackError as Error).message}`);
      }
    }
  }

  // Device Models APIs
  async getDeviceModels(): Promise<any[]> {
    return this.getDeviceModelsDirect();
  }

  private async getDeviceModelsDirect(): Promise<any[]> {
    const response = await this.fetchWithAuth('/device-models');
    const data = await this.handleResponse<{ success: boolean; data: { deviceModels: any[] } }>(response);
    return data.data.deviceModels;
  }

  async createDeviceModel(modelData: { 
    name: string; 
    description?: string; 
    isActive?: boolean; 
    displayOrder?: number 
  }): Promise<any> {
    const response = await this.fetchWithAuth('/device-models', {
      method: 'POST',
      body: JSON.stringify(modelData),
    });
    const data = await this.handleResponse<{ success: boolean; data: { deviceModel: any } }>(response);
    return data.data.deviceModel;
  }

  async updateDeviceModel(id: string, modelData: { 
    name?: string; 
    description?: string; 
    isActive?: boolean; 
    displayOrder?: number 
  }): Promise<any> {
    const response = await this.fetchWithAuth(`/device-models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(modelData),
    });
    const data = await this.handleResponse<{ success: boolean; data: { deviceModel: any } }>(response);
    return data.data.deviceModel;
  }

  async deleteDeviceModel(id: string): Promise<void> {
    const response = await this.fetchWithAuth(`/device-models/${id}`, {
      method: 'DELETE',
    });
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }

  // Customer Types APIs
  async getCustomerTypes(): Promise<any[]> {
    return this.getCustomerTypesDirect();
  }

  private async getCustomerTypesDirect(): Promise<any[]> {
    const response = await this.fetchWithAuth('/customer-types');
    const data = await this.handleResponse<{ success: boolean; data: { customerTypes: any[] } }>(response);
    return data.data.customerTypes;
  }

  async createCustomerType(typeData: { 
    name: string; 
    description?: string; 
    colorCode?: string; 
    isActive?: boolean; 
    displayOrder?: number 
  }): Promise<any> {
    const response = await this.fetchWithAuth('/customer-types', {
      method: 'POST',
      body: JSON.stringify(typeData),
    });
    const data = await this.handleResponse<{ success: boolean; data: { customerType: any } }>(response);
    return data.data.customerType;
  }

  async updateCustomerType(id: string, typeData: { 
    name?: string; 
    description?: string; 
    colorCode?: string; 
    isActive?: boolean; 
    displayOrder?: number 
  }): Promise<any> {
    const response = await this.fetchWithAuth(`/customer-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(typeData),
    });
    const data = await this.handleResponse<{ success: boolean; data: { customerType: any } }>(response);
    return data.data.customerType;
  }

  async deleteCustomerType(id: string): Promise<void> {
    const response = await this.fetchWithAuth(`/customer-types/${id}`, {
      method: 'DELETE',
    });
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }

  // User APIs (for agents)
  async getUsers(): Promise<User[]> {
    const response = await this.fetchWithAuth('/users');
    const data = await this.handleResponse<{ users: User[] }>(response);
    return data.users;
  }

  async getAgents(): Promise<User[]> {
    console.log('🔍 API: getAgents called');
    const response = await this.fetchWithAuth('/agents');
    console.log('🔍 API: getAgents response status:', response.status);
    
    const apiResponse = await this.handleResponse<{ success: boolean; data: { agents: any[] } }>(response);
    console.log('🔍 API: getAgents parsed response:', apiResponse);
    
    // Transform agents to match frontend interface with presence information
    const agents: User[] = apiResponse.data.agents.map((agent: any) => ({
      id: agent.id,
      email: agent.email,
      name: agent.name || `${agent.firstName} ${agent.lastName}`,
      role: 'agent' as const,
      roleName: agent.roleName || 'Tier1',
      firstName: agent.firstName,
      lastName: agent.lastName,
      isOnline: agent.isOnline || false, // Include real-time online status
      lastSeen: agent.lastSeen || agent.lastLogin, // Include last seen time
      avatar: agent.avatar || agent.avatarUrl,
      avatarUrl: agent.avatar || agent.avatarUrl,
    }));
    
    console.log('🔍 API: getAgents transformed agents with presence:', agents);
    return agents;
  }

  async getAgentStatus(): Promise<any> {
    console.log('🔍 API: getAgentStatus called');
    const response = await this.fetchWithAuth('/agents/status');
    console.log('🔍 API: getAgentStatus response status:', response.status);
    
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    console.log('🔍 API: getAgentStatus parsed response:', apiResponse);
    
    return apiResponse.data;
  }

  async reassignTicket(ticketId: string, agentId: string): Promise<Ticket> {
    const response = await this.fetchWithAuth(`/tickets/${ticketId}`, {
      method: 'PUT',
      body: JSON.stringify({ agentId: agentId || null }),
    });
    
    const apiResponse = await this.handleResponse<{ success: boolean; data: { ticket: any } }>(response);
    
    // Transform ticket to match frontend interface
    const ticket: Ticket = {
      id: apiResponse.data.ticket.id,
      ticketNumber: apiResponse.data.ticket.ticketNumber || apiResponse.data.ticket.id,
      title: apiResponse.data.ticket.title,
      description: apiResponse.data.ticket.description,
      status: apiResponse.data.ticket.status,
      priority: apiResponse.data.ticket.priority,
      category: apiResponse.data.ticket.category?.name || apiResponse.data.ticket.categoryId,
      customerId: apiResponse.data.ticket.customerId,
      agentId: apiResponse.data.ticket.agentId,
      createdAt: apiResponse.data.ticket.createdAt,
      updatedAt: apiResponse.data.ticket.updatedAt,
      customer: apiResponse.data.ticket.customer ? {
        id: apiResponse.data.ticket.customer.id,
        email: apiResponse.data.ticket.customer.email,
        name: `${apiResponse.data.ticket.customer.firstName} ${apiResponse.data.ticket.customer.lastName}`,
        role: 'customer' as const
      } : undefined,
      agent: apiResponse.data.ticket.agent ? {
        id: apiResponse.data.ticket.agent.id,
        email: apiResponse.data.ticket.agent.email,
        name: `${apiResponse.data.ticket.agent.firstName} ${apiResponse.data.ticket.agent.lastName}`,
        role: 'agent' as const
      } : undefined,
    };
    
    return ticket;
  }

  // Get customer status for a ticket
  async getCustomerStatus(ticketId: string): Promise<{ isOnline: boolean; lastSeen: string | null; customerId: string | null }> {
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/customer-status`);
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    
    // Handle nested customerStatus structure from backend
    const statusData = apiResponse.data.customerStatus || apiResponse.data;
    
    return {
      isOnline: statusData.isOnline || false,
      lastSeen: statusData.lastSeen || null,
      customerId: statusData.customerId || null
    };
  }

  // Close ticket (for customers - no authentication required)
  async closeTicket(ticketId: string, reason?: 'manual' | 'timeout'): Promise<void> {
    const response = await fetch(`${this.baseURL}/tickets/${ticketId}/close`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason: reason || 'manual' }),
    });
    
    await this.handleResponse<{ success: boolean; data: { message: string } }>(response);
  }

  // Submit feedback without closing ticket (for customers - no authentication required)
  async submitTicketFeedback(ticketId: string, resolution: 'resolved' | 'not_resolved' | 'partially_resolved'): Promise<void> {
    console.log('🔧 DEBUG: submitTicketFeedback called with:', { ticketId, resolution });
    console.log('🔧 DEBUG: API URL will be:', `${this.baseURL}/tickets/${ticketId}/feedback`);
    console.log('🔧 DEBUG: Request body will be:', JSON.stringify({ resolution }));
    
    try {
      const response = await fetch(`${this.baseURL}/tickets/${ticketId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resolution }),
      });
      
      console.log('🔧 DEBUG: submitTicketFeedback response status:', response.status);
      console.log('🔧 DEBUG: submitTicketFeedback response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔧 DEBUG: submitTicketFeedback error response:', errorText);
        console.error('🔧 DEBUG: Response status:', response.status);
        console.error('🔧 DEBUG: Response statusText:', response.statusText);
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
      
      const responseData = await response.json();
      console.log('🔧 DEBUG: submitTicketFeedback response data:', responseData);
      console.log('🔧 DEBUG: submitTicketFeedback completed successfully');
    } catch (error) {
      console.error('🔧 DEBUG: submitTicketFeedback error:', error);
      console.error('🔧 DEBUG: Error type:', typeof error);
      console.error('🔧 DEBUG: Error instanceof Error:', error instanceof Error);
      console.error('🔧 DEBUG: Error name:', error instanceof Error ? error.name : 'unknown');
      console.error('🔧 DEBUG: Error message:', error instanceof Error ? error.message : String(error));
      throw error;
    }
  }

  // Close ticket with feedback (for customers - no authentication required)
  async closeTicketWithFeedback(ticketId: string, resolution: 'resolved' | 'not_resolved' | 'partially_resolved'): Promise<void> {
    console.log('🔧 DEBUG: closeTicketWithFeedback called with:', { ticketId, resolution });
    console.log('🔧 DEBUG: API URL will be:', `${this.baseURL}/tickets/${ticketId}/close-with-feedback`);
    
    try {
      const response = await fetch(`${this.baseURL}/tickets/${ticketId}/close-with-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resolution }),
      });
      
      console.log('🔧 DEBUG: Response status:', response.status);
      console.log('🔧 DEBUG: Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔧 DEBUG: Error response text:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const responseData = await this.handleResponse<{ success: boolean; data: { message: string } }>(response);
      console.log('🔧 DEBUG: Successful response:', responseData);
    } catch (error) {
      console.error('🔧 DEBUG: closeTicketWithFeedback error:', error);
      throw error;
    }
  }

  // Insights API - Get analytics data
  async getInsights(filters?: {
    timeRange?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    startDate?: string;
    endDate?: string;
    agentId?: string;
    category?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    
    const queryString = params.toString();
    const url = `/insights${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    
    return apiResponse.data;
  }

  // Customer APIs
  async getCustomers(filters?: {
    search?: string;
    country?: string;
    customerType?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    limit?: number;
  }): Promise<{ customers: any[]; pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number; } }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
    }
    
    const queryString = params.toString();
    const url = `/customers${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { customers: any[]; pagination: any } }>(response);
    
    return apiResponse.data;
  }

  async getCustomer(identifier: string): Promise<any> {
    const response = await this.fetchWithAuth(`/customers/${identifier}`);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { customer: any } }>(response);
    return apiResponse.data.customer;
  }

  // Debug method to fix customer company association
  async fixCustomerCompanyAssociation(email: string): Promise<any> {
    const response = await this.fetchWithAuth(`/debug/fix-customer-company/${email}`, {
      method: 'POST',
    });
    return await this.handleResponse<any>(response);
  }

  // Debug method to check customer data
  async debugCustomerData(email: string): Promise<any> {
    const response = await this.fetchWithAuth(`/debug/customer-data/${email}`);
    return await this.handleResponse<any>(response);
  }

  async updateCustomer(identifier: string, updates: any): Promise<any> {
    const response = await this.fetchWithAuth(`/customers/${identifier}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { customer: any } }>(response);
    return apiResponse.data.customer;
  }

  async deleteCustomer(identifier: string): Promise<void> {
    const response = await this.fetchWithAuth(`/customers/${identifier}`, {
      method: 'DELETE',
    });
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }

  // Customer-specific APIs
  async getCustomerTickets(customerId: string): Promise<{ tickets: any[] }> {
    const response = await this.fetchWithAuth(`/customers/${customerId}/tickets`, {
      method: 'GET',
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { tickets: any[] } }>(response);
    return apiResponse.data;
  }

  async updateCustomerProfile(profileData: any): Promise<any> {
    const response = await this.fetchWithAuth('/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    return apiResponse.data;
  }

  // Device APIs
  async getDevices(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    model?: string;
    status?: string;
    customerId?: string;
    companyId?: string;
  }): Promise<{ devices: any[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    const queryString = params.toString();
    const url = `/devices${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { devices: any[]; pagination: any } }>(response);
    
    return apiResponse.data;
  }

  async getDevice(id: string): Promise<any> {
    const response = await this.fetchWithAuth(`/devices/${id}`);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { device: any } }>(response);
    return apiResponse.data.device;
  }

  async createDevice(deviceData: {
    customerId: string;
    model: string;
    serialNumber: string;
    warrantyExpires?: string;
    invoiceNumber?: string;
    invoiceDate?: string;
    comments?: string;
  }): Promise<any> {
    const response = await this.fetchWithAuth('/devices', {
      method: 'POST',
      body: JSON.stringify(deviceData),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { device: any } }>(response);
    return apiResponse.data.device;
  }

  async updateDevice(id: string, updates: {
    warrantyExpires?: string;
    invoiceNumber?: string;
    invoiceDate?: string;
    comments?: string;
  }): Promise<any> {
    const response = await this.fetchWithAuth(`/devices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { device: any } }>(response);
    return apiResponse.data.device;
  }

  async deleteDevice(id: string): Promise<void> {
    await this.fetchWithAuth(`/devices/${id}`, {
      method: 'DELETE',
    });
  }

  async getDeviceStats(): Promise<any> {
    const response = await this.fetchWithAuth('/devices/stats');
    const apiResponse = await this.handleResponse<{ success: boolean; data: { stats: any } }>(response);
    return apiResponse.data.stats;
  }

  async getDeviceServices(deviceId: string): Promise<{
    device: {
      id: string;
      model: string;
      serialNumber: string;
      customerName: string;
    };
    services: any[];
    totalServices: number;
  }> {
    const response = await this.fetchWithAuth(`/devices/${deviceId}/services`);
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    return apiResponse.data;
  }

  async getCompanyServices(companyId: string): Promise<{
    company: {
      id: string;
      name: string;
    };
    devices: {
      id: string;
      model: string;
      serialNumber: string;
      customerName: string;
    }[];
    services: any[];
    totalServices: number;
    totalDevices: number;
  }> {
    const response = await this.fetchWithAuth(`/companies/${companyId}/services`);
    const apiResponse = await this.handleResponse<{ success: boolean; data: any }>(response);
    return apiResponse.data;
  }

  // Generic HTTP methods for convenience
  async get(url: string): Promise<any> {
    const response = await this.fetchWithAuth(url);
    return this.handleResponse<any>(response);
  }

  async post(url: string, data?: any): Promise<any> {
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
    return this.handleResponse<any>(response);
  }

  async put(url: string, data?: any): Promise<any> {
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
    return this.handleResponse<any>(response);
  }

  async delete(url: string): Promise<any> {
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    return this.handleResponse<any>(response);
  }

  // Company/Account APIs
  async getCompanies(filters?: {
    search?: string;
    country?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    limit?: number;
  }): Promise<{ companies: any[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    const queryString = params.toString();
    const url = `/companies${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithAuth(url);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { companies: any[]; pagination: any } }>(response);
    
    return apiResponse.data;
  }

  async getCompany(id: string): Promise<any> {
    const response = await this.fetchWithAuth(`/companies/${id}`);
    const apiResponse = await this.handleResponse<{ success: boolean; data: { company: any } }>(response);
    return apiResponse.data.company;
  }

  async createCompany(companyData: {
    name: string;
    aliases?: string[];
    description?: string;
    primaryEmail?: string;
    primaryPhone?: string;
    website?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  }): Promise<any> {
    const response = await this.fetchWithAuth('/companies', {
      method: 'POST',
      body: JSON.stringify(companyData),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { company: any } }>(response);
    return apiResponse.data.company;
  }

  async updateCompany(id: string, updates: {
    name?: string;
    aliases?: string[];
    description?: string;
    primaryEmail?: string;
    primaryPhone?: string;
    website?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  }): Promise<any> {
    const response = await this.fetchWithAuth(`/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { company: any } }>(response);
    return apiResponse.data.company;
  }

  async deleteCompany(id: string): Promise<void> {
    await this.fetchWithAuth(`/companies/${id}`, {
      method: 'DELETE',
    });
  }

  async fuzzyMatchCompany(companyName: string, threshold = 75): Promise<{ inputName: string; matches: any[]; threshold: number }> {
    const response = await this.fetchWithAuth('/companies/fuzzy-match', {
      method: 'POST',
      body: JSON.stringify({ companyName, threshold }),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { inputName: string; matches: any[]; threshold: number } }>(response);
    return apiResponse.data;
  }

  async assignCustomerToCompany(companyId: string, customerId: string): Promise<void> {
    await this.fetchWithAuth(`/companies/${companyId}/assign-customer`, {
      method: 'POST',
      body: JSON.stringify({ customerId }),
    });
  }

  async unassignCustomerFromCompany(companyId: string, customerId: string): Promise<void> {
    await this.fetchWithAuth(`/companies/${companyId}/unassign-customer`, {
      method: 'POST',
      body: JSON.stringify({ customerId }),
    });
  }

  async suggestCompanyMatch(ticketId: string): Promise<{ inputCompanyName: string; suggestions: any[]; ticket: any }> {
    const response = await this.fetchWithAuth(`/tickets/${ticketId}/suggest-company-match`, {
      method: 'POST',
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { inputCompanyName: string; suggestions: any[]; ticket: any } }>(response);
    return apiResponse.data;
  }

  async confirmCompanyMatch(ticketId: string, companyId: string): Promise<void> {
    await this.fetchWithAuth(`/tickets/${ticketId}/confirm-company-match`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  }

  // Pending company matches
  async getPendingCompanyMatches(): Promise<any[]> {
    const response = await this.fetchWithAuth('/companies/pending-matches');
    const data = await this.handleResponse<{ success: boolean; data: any[] }>(response);
    return data.data;
  }

  async reviewCompanyMatch(matchId: string, action: 'approve' | 'reject'): Promise<void> {
    const response = await this.fetchWithAuth(`/companies/pending-matches/${matchId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
    
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }

  async suggestCompanyRealtime(companyName: string, threshold = 50): Promise<{ inputName: string; suggestions: any[]; threshold: number }> {
    const response = await fetch(`${this.baseURL}/companies/suggest-realtime`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ companyName, threshold }),
    });
    const apiResponse = await this.handleResponse<{ success: boolean; data: { inputName: string; suggestions: any[]; threshold: number } }>(response);
    return apiResponse.data;
  }

  // NVDB Sync APIs
  async syncNVDBData(options: { forceCustomers?: boolean; cleanSync?: boolean } = {}): Promise<{
    success: boolean;
    message: string;
    stats: {
      processed: number;
      companiesCreated: number;
      companiesUpdated: number;
      customersCreated: number;
      customersUpdated: number;
      customersSkipped?: number;
      companiesDeleted?: number;
      customersDeleted?: number;
      errors: number;
      warnings: string[];
    };
    duration: number;
  }> {
    const response = await this.fetchWithAuth('/admin/nvdb-sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        syncType: 'full',
        options: options
      }),
    });
    return await this.handleResponse(response);
  }

  async syncNVDBDevices(options: { forceUpdate?: boolean } = {}): Promise<{
    success: boolean;
    message: string;
    stats: {
      processed: number;
      companiesCreated: number;
      devicesCreated: number;
      devicesUpdated: number;
      errors: number;
      warnings: string[];
    };
    duration: number;
  }> {
    const response = await this.fetchWithAuth('/admin/nvdb-sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        syncType: 'devices',
        options: options
      }),
    });
    return await this.handleResponse(response);
  }

  // NVDB Sync V2 - New API with serial number correlation
  async syncNVDBV2(options: Record<string, any> = {}): Promise<{
    success: boolean;
    message: string;
    stats: {
      companiesCreated: number;
      companiesUpdated: number;
      customersCreated: number;
      customersUpdated: number;
      devicesCreated: number;
      devicesUpdated: number;
      errors: number;
      warnings: string[];
    };
    duration: string;
  }> {
    const response = await this.fetchWithAuth('/nvdb/sync-v2', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    return await this.handleResponse(response);
  }

  // Enhanced NVDB Sync APIs
  async enhancedNVDBSync(options: {
    syncType?: 'full' | 'companies' | 'customers' | 'devices';
    isDryRun?: boolean;
    options?: any;
  } = {}): Promise<{
    success: boolean;
    message: string;
    syncId?: string;
    stats?: {
      total_nvdb_records: number;
      records_created: number;
      records_updated: number;
      records_unchanged: number;
      records_soft_deleted: number;
      errors_count: number;
      companies: { created: number; updated: number; unchanged: number; soft_deleted: number };
      customers: { created: number; updated: number; unchanged: number; soft_deleted: number };
      devices: { created: number; updated: number; unchanged: number; soft_deleted: number };
    };
    duration?: number;
    isDryRun?: boolean;
    error?: {
      code: string;
      message: string;
    };
    details?: string;
  }> {
    const { isDryRun, ...bodyOptions } = options;
    const queryParams = isDryRun ? '?dryRun=true' : '';
    
    const response = await this.fetchWithAuth(`/admin/nvdb-sync${queryParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyOptions),
    });
    return await this.handleResponse(response);
  }

  async getNVDBSyncHistory(limit: number = 10): Promise<{
    success: boolean;
    data: {
      history: Array<{
        sync_id: string;
        sync_type: string;
        started_at: string;
        completed_at: string | null;
        status: string;
        is_dry_run: boolean;
        total_nvdb_records: number;
        records_created: number;
        records_updated: number;
        records_unchanged: number;
        records_soft_deleted: number;
        errors_count: number;
        duration_seconds: number;
        triggered_by: string;
      }>;
    };
  }> {
    const response = await this.fetchWithAuth(`/admin/nvdb-sync/history?limit=${limit}`, {
      method: 'GET',
    });
    return await this.handleResponse(response);
  }

  async getNVDBSyncDetails(syncId: string): Promise<{
    success: boolean;
    data: {
      audit: {
        sync_id: string;
        sync_type: string;
        started_at: string;
        completed_at: string | null;
        status: string;
        is_dry_run: boolean;
        total_nvdb_records: number;
        records_created: number;
        records_updated: number;
        records_unchanged: number;
        records_soft_deleted: number;
        errors_count: number;
        duration_seconds: number;
        triggered_by: string;
        sync_options: any;
        error_details: any;
      };
      changes: Array<{
        change_id: string;
        entity_type: string;
        entity_id: string | null;
        nvdb_id: string;
        action: string;
        nvdb_data: any;
        local_data_before: any;
        local_data_after: any;
        fields_changed: string[];
        error_message: string | null;
        created_at: string;
      }>;
    };
  }> {
    const response = await this.fetchWithAuth(`/admin/nvdb-sync/${syncId}`, {
      method: 'GET',
    });
    return await this.handleResponse(response);
  }

  async getNVDBSyncStatus(): Promise<{
    success: boolean;
    status: {
      totalCompanies: number;
      totalCustomers: number;
      lastSyncAt: string | null;
      apiStatus: string;
    };
  }> {
    const response = await this.fetchWithAuth('/nvdb/status');
    return await this.handleResponse(response);
  }

  // NVDB Scheduler APIs
  async getNVDBSchedulerSettings(): Promise<{
    success: boolean;
    data: {
      settings: {
        is_enabled: boolean;
        sync_times: string[];
        timezone: string;
        sync_type: string;
        is_dry_run: boolean;
        last_run_at?: string;
        next_run_at?: string;
      };
      status: {
        isRunning: boolean;
        hasInterval: boolean;
      };
    };
  }> {
    const response = await this.fetchWithAuth('/admin/nvdb-scheduler/settings', {
      method: 'GET',
    });
    return await this.handleResponse(response);
  }

  async updateNVDBSchedulerSettings(settings: {
    is_enabled: boolean;
    sync_times: string[];
    timezone?: string;
    sync_type?: string;
    is_dry_run?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.fetchWithAuth('/admin/nvdb-scheduler/settings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    });
    return await this.handleResponse(response);
  }

  async getNVDBScheduledRuns(limit: number = 50): Promise<{
    success: boolean;
    data: {
      runs: Array<{
        run_id: string;
        scheduled_time: string;
        actual_run_time: string;
        sync_id: string | null;
        status: string;
        error_message: string | null;
        duration_seconds: number | null;
        records_created: number | null;
        records_updated: number | null;
        records_unchanged: number | null;
        errors_count: number | null;
      }>;
    };
  }> {
    const response = await this.fetchWithAuth(`/admin/nvdb-scheduler/runs?limit=${limit}`, {
      method: 'GET',
    });
    return await this.handleResponse(response);
  }

  // User Preferences APIs
  async getTicketListPreferences(): Promise<{
    itemsPerPage: number;
    sortBy: string;
    sortOrder: string;
    statusFilter: string;
    priorityFilter: string;
    searchTerm: string;
  }> {
    const response = await this.fetchWithAuth('/user-preferences/ticket-list');
    const result = await this.handleResponse<{ success: boolean; data: { preferences: any } }>(response);
    return result.data.preferences;
  }

  async saveTicketListPreferences(preferences: {
    itemsPerPage?: number;
    sortBy?: string;
    sortOrder?: string;
    statusFilter?: string;
    priorityFilter?: string;
    searchTerm?: string;
  }): Promise<void> {
    const response = await this.fetchWithAuth('/user-preferences/ticket-list', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preferences }),
    });
    await this.handleResponse(response);
  }

  async getCustomerListPreferences(): Promise<{
    itemsPerPage: number;
    sortBy: string;
    sortOrder: string;
    searchTerm: string;
    countryFilter: string;
    customerTypeFilter: string;
  }> {
    const response = await this.fetchWithAuth('/user-preferences/customer-list');
    const result = await this.handleResponse<{ success: boolean; data: { preferences: any } }>(response);
    return result.data.preferences;
  }

  async saveCustomerListPreferences(preferences: {
    itemsPerPage?: number;
    sortBy?: string;
    sortOrder?: string;
    searchTerm?: string;
    countryFilter?: string;
    customerTypeFilter?: string;
  }): Promise<void> {
    const response = await this.fetchWithAuth('/user-preferences/customer-list', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preferences }),
    });
    await this.handleResponse(response);
  }

  async getDeviceListPreferences(): Promise<{
    itemsPerPage: number;
    sortBy: string;
    sortOrder: string;
    searchTerm: string;
    modelFilter: string;
    statusFilter: string;
  }> {
    const response = await this.fetchWithAuth('/user-preferences/device-list');
    const result = await this.handleResponse<{ success: boolean; data: { preferences: any } }>(response);
    return result.data.preferences;
  }

  async saveDeviceListPreferences(preferences: {
    itemsPerPage?: number;
    sortBy?: string;
    sortOrder?: string;
    searchTerm?: string;
    modelFilter?: string;
    statusFilter?: string;
  }): Promise<void> {
    const response = await this.fetchWithAuth('/user-preferences/device-list', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preferences }),
    });
    await this.handleResponse<void>(response);
  }

  async getCompanyListPreferences(): Promise<{
    itemsPerPage: number;
    sortBy: string;
    sortOrder: string;
    searchTerm: string;
    countryFilter: string;
  }> {
    const response = await this.fetchWithAuth('/user-preferences/company-list');
    const apiResponse = await this.handleResponse<{ success: boolean; preferences: any }>(response);
    return apiResponse.preferences || {
      itemsPerPage: 50,
      sortBy: 'name',
      sortOrder: 'asc',
      searchTerm: '',
      countryFilter: '',
    };
  }

  async saveCompanyListPreferences(preferences: {
    itemsPerPage?: number;
    sortBy?: string;
    sortOrder?: string;
    searchTerm?: string;
    countryFilter?: string;
  }): Promise<void> {
    const response = await this.fetchWithAuth('/user-preferences/company-list', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
    await this.handleResponse<void>(response);
  }

  // ==========================================
  // QUICK MESSAGES API
  // ==========================================

  async getQuickMessages(language: string = 'en'): Promise<any[]> {
    const response = await this.fetchWithAuth(`/quick-messages?language=${language}`);
    const data = await this.handleResponse<{ success: boolean; data: any[] }>(response);
    return data.data;
  }

  async getQuickMessagesAdmin(): Promise<any[]> {
    const response = await this.fetchWithAuth('/quick-messages/admin');
    const data = await this.handleResponse<{ success: boolean; data: any[] }>(response);
    return data.data;
  }

  async createQuickMessage(messageData: { 
    title: string; 
    content: string; 
    category: string; 
    language?: string; 
    isActive?: boolean; 
    displayOrder?: number 
  }): Promise<any> {
    const response = await this.fetchWithAuth('/quick-messages', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
    const data = await this.handleResponse<{ success: boolean; data: { quickMessage: any } }>(response);
    return data.data.quickMessage;
  }

  async updateQuickMessage(id: string, messageData: { 
    title?: string; 
    content?: string; 
    category?: string; 
    language?: string; 
    isActive?: boolean; 
    displayOrder?: number 
  }): Promise<any> {
    const response = await this.fetchWithAuth(`/quick-messages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(messageData),
    });
    const data = await this.handleResponse<{ success: boolean; data: { quickMessage: any } }>(response);
    return data.data.quickMessage;
  }

  async deleteQuickMessage(id: string): Promise<void> {
    const response = await this.fetchWithAuth(`/quick-messages/${id}`, {
      method: 'DELETE',
    });
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }

  // Parts Management APIs
  async getPartsAdmin(): Promise<any[]> {
    const response = await this.fetchWithAuth('/parts');
    const data = await this.handleResponse<{ success: boolean; parts: any[] }>(response);
    return data.parts;
  }

  async createPart(partData: { 
    part_number: string; 
    item_name: string; 
    category?: string; 
    description?: string 
  }): Promise<any> {
    const response = await this.fetchWithAuth('/parts', {
      method: 'POST',
      body: JSON.stringify(partData),
    });
    const data = await this.handleResponse<{ success: boolean; part: any }>(response);
    return data.part;
  }

  async updatePart(partNumber: string, partData: { 
    item_name?: string; 
    category?: string; 
    description?: string 
  }): Promise<any> {
    const response = await this.fetchWithAuth(`/parts/${partNumber}`, {
      method: 'PUT',
      body: JSON.stringify(partData),
    });
    const data = await this.handleResponse<{ success: boolean; part: any }>(response);
    return data.part;
  }

  async deletePart(partNumber: string): Promise<void> {
    const response = await this.fetchWithAuth(`/parts/${partNumber}`, {
      method: 'DELETE',
    });
    await this.handleResponse<{ success: boolean; message: string }>(response);
  }
}

export const apiService = new ApiService();
export default apiService; 